import Input from '@service-victoria/component-library/components/Input';
import { ErrorLabel } from '@service-victoria/component-library/components/Input/styled';
import Text from '@service-victoria/component-library/components/Text';
import SummaryItem from 'Components/SummaryItem';
import { useField } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Spacer24 } from 'steps/styled';
import { retryableFn, sleep } from 'utils/fns';
import { logger } from 'utils';
import { getAbnSearchApiDomain } from 'utils/url';
import { isValidABN } from 'utils/validations';
import { ABNService } from './service';
import * as Styled from './styled';

export type ComponentProps = {
  fieldName: string;
  label: string;
};

// attach ABN details in the option so we don't need to query it again when option is selected
const service = new ABNService(getAbnSearchApiDomain());

// Automatically retry the fetch
const fetchAbnDetails = retryableFn(service.getABNDetails);

const maskingOptions = {
  formatChars: {
    x: '[0-9]'
  },
  mask: 'xx xxx xxx xxx'
};

const SERVICE_ERROR = '~ Service Error ~';

export const Lookup: React.FC<ComponentProps> = ({ fieldName, label }) => {
  const [{ value: abnNumber }, { touched, error: numberError }, { setValue: setAbn }] =
    useField<string>(`${fieldName}.number`);
  const [{ value: businessName }, { error: nameError }, { setValue: setBusinessName }] = useField<
    string | null
  >(`${fieldName}.name`);

  const [isSearching, setSearching] = useState(false);
  const knownBusinessCache = useRef<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      if (isValidABN(abnNumber)) {
        if (Object.getOwnPropertyNames(knownBusinessCache.current).indexOf(abnNumber) > -1) {
          setBusinessName(knownBusinessCache.current[abnNumber]);
        } else {
          setSearching(true);
          if (businessName !== SERVICE_ERROR) {
            setBusinessName(null);
          }
          try {
            // Avoid service being too fast
            const [details] = await Promise.all([fetchAbnDetails(abnNumber), sleep(500)]);

            const name = details?.entityDetails?.organizationName || '';
            setBusinessName(name);
            knownBusinessCache.current[abnNumber] = name;
          } catch (e) {
            // Business not found is handled in above case, this indicates there was a service error
            if (businessName !== SERVICE_ERROR) {
              logger.error('Error retrieving ABN Details from service', e);
              setBusinessName(SERVICE_ERROR);
            }
          } finally {
            setSearching(false);
          }
        }
      } else if (businessName !== null) {
        setBusinessName(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abnNumber, businessName]);

  const resetAbn = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setAbn('', false);
    setBusinessName(null, false);
  };

  return (
    <Styled.Wrapper>
      <Input
        name={`${fieldName}.number`}
        label={label}
        maskingOptions={maskingOptions}
        type='search'
        autoComplete='off'
        hint='We’ll use your ABN and info from the Australian Business Registry (ABR) to check if your business is eligible.'
      />
      {isSearching && <Styled.StyledSpinner />}
      {businessName === null && touched && !numberError && nameError && (
        <ErrorLabel aria-invalid aria-labelledby={`${fieldName}.number.label`}>
          {nameError}
        </ErrorLabel>
      )}
      {businessName !== null && businessName !== SERVICE_ERROR && (
        <>
          <Spacer24 />
          <>
            <Styled.Heading>
              <Styled.TickWrapper />
              Business found
            </Styled.Heading>
            <SummaryItem label='ABN' value={abnNumber} />
            {businessName !== '' && (
              <SummaryItem label='Business entity name' value={businessName} />
            )}
            <Text.Body style={{ marginTop: 0 }}>
              Not your business? <Text.Link onClick={resetAbn}>Re-enter details</Text.Link>
            </Text.Body>
          </>
        </>
      )}
    </Styled.Wrapper>
  );
};

export default Lookup;
